import { Dayjs } from 'dayjs'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DateTimePicker, DateTimePickerProps } from '@mui/x-date-pickers/DateTimePicker'
import { FormControl } from '@mui/material'
import { SxProps, Theme } from '@mui/material/styles'

import TextInput from './TextInput'

import { adapterLocaleMap } from '@/utils/locale'

export type DateTimePickerInputProps = Omit<DateTimePickerProps<Dayjs, Dayjs>, 'renderInput' | 'value'> & {
    /**
     * If `true`, the component is displayed in an error state.
     * @default false
     */
    error?: boolean
    /**
     * If true, the input will take up the full width of its container.
     * @default false
     */
    fullWidth?: boolean
    /**
     * The helper text content.
     */
    helperText?: React.ReactNode
    /**
     * Name attribute of the `input` element.
     */
    name?: string
    /**
     * Callback fired when the input is blur.
     *
     * @param {object} event The event source of the callback.
     */
    /**
     * The value of the picker
     */
    onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
    value: Dayjs | null
    sx?: SxProps<Theme>
}

export function DateTimePickerInput(props: DateTimePickerInputProps) {
    const { error, fullWidth, helperText, name, onBlur, sx, ...rest } = props
    const { i18n } = useTranslation()

    return (
        <FormControl fullWidth sx={sx}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocaleMap[i18n.language.split('-')[0]]}>
                <DateTimePicker
                    {...rest}
                    ampm={false}
                    onError={console.log}
                    OpenPickerButtonProps={{
                        size: 'small',
                    }}
                    renderInput={(params) => (
                        <TextInput
                            {...params}
                            inputProps={{
                                ...params.inputProps,
                                'data-testid': 'datetime-input',
                            }}
                            fullWidth={fullWidth}
                            helperText={helperText}
                            name={name}
                            onBlur={onBlur}
                            error={params.error || error}
                        />
                    )}
                />
            </LocalizationProvider>
        </FormControl>
    )
}

export default DateTimePickerInput
